import { atom } from 'jotai';
import { atomFamily, atomWithReset } from 'jotai/utils';
import { atomWithQuery } from 'jotai-tanstack-query';

import { contextNavAtom } from '_atoms/navigation';
import { respondentAtom } from '_atoms/respondent';
import { surveyGetQuery } from '_queries/surveyQueries';
import {
  applyRandomSeedToSurvey,
  createElementContextMap,
  createFlatMapOfElements,
  createFlatMapOfStepIds,
  createStepMap,
  removeLoginCodeEmailRequestFieldsFromSurvey
} from '_helpers/surveyHelpers';

import type { Respondent } from '@/types/respondent';
import type {Element, ElementContext, Survey, SurveyView, OptionSet, Step} from '@/types/survey';

/** The id of the currently active survey. A string-formatted uuid or an empty string when there is no active survey. */
export const surveyIdAtom = atomWithReset<string | null>(null);

/**
 * Fetches and materializes a compact "survey view" for the active survey ID.
 * When there is an active survey (or a single survey in the batch), this atom:
 *  - fetches the survey,
 *  - applies respondent-specific transforms (login-code removal, randomization of item orders),
 *  - builds a compact view used by the UI (elements, element contexts, option sets, step flags).
 */
export const surveyViewAtom = atomWithQuery<SurveyView | null>((get) => {
  const surveyId: string | null = get(surveyIdAtom);
  const respondent: Respondent = get(respondentAtom);

  return {
    queryKey: ['survey-view', surveyId],
    queryFn: async () => {
      if (!surveyId || !respondent?.id) return null;

      const response = await surveyGetQuery(surveyId);
      const raw: Survey = response.data;
      const cleaned: Survey = respondent.hasSavedLoginCode ? removeLoginCodeEmailRequestFieldsFromSurvey(raw) : raw;
      const survey: Survey = applyRandomSeedToSurvey(respondent.randomSeed, cleaned);

      const elements: Element[] = createFlatMapOfElements(survey.steps);
      const flatMapOfStepIds: string[] = createFlatMapOfStepIds(survey.steps);

      return {
        id: surveyId,
        elements: elements,
        contextMap: createElementContextMap(elements, flatMapOfStepIds),
        hasProgressBarLabels: survey.steps.some((step: Step) => step?.progressBar?.label) ?? false,
        optionSetMap: new Map(survey.optionSets.map((o: OptionSet) => [o.id, o])),
        stepMap: createStepMap(survey.steps),
        firstStepId: survey.steps.at(0)?.id ?? null,
        lastStepId: survey.steps.at(-1)?.id ?? null,
      } as SurveyView;
    },
    enabled: !!surveyId && !!respondent?.id,
    staleTime: Infinity, // never refetch unless manually
    gcTime: Infinity,    // never garbage collect
  };
});

/**
 * Elements on the current page, in render order.
 * Uses the current element's pageStart/pageEnd to slice the global `elements` array.
 */
export const pageElementsAtom = atom<Element[]>((get) => {
  const elements: Element[] = get(surveyViewAtom).data?.elements ?? [];
  const currentContext: ElementContext | null = get(contextNavAtom).currentContext;

  if (!currentContext || elements.length == 0) return [];

  return elements.slice(currentContext.pageStart, currentContext.pageEnd + 1);
});

// Resolves to `SurveyView | null` and truly returns a Promise.
// TS is happy because we await a Promise-like value.
export const waitForSurveyViewAtom = atom<Promise<SurveyView | null>>(async (get) => {
  while (true) {
    const surveyId: string | null = get(surveyIdAtom);
    const respondent: Respondent = get(respondentAtom);
    const { data, error } = get(surveyViewAtom); // query result (sync object)

    // If the query errored or is missing required preconditions, return null.
    if (!surveyId || !respondent?.id || error) return null;

    // If the query is enabled and has data, return it
    if (data) return data as SurveyView;

    // Yield to the event loop so Jotai/query can progress
    await new Promise((r) => setTimeout(r, 20));
  }
});

/**
 * Create a derived atom that retrieves an option set for a given option set ID.
 *
 * @param optionSetId - The ID of the OptionSet to retrieve.
 * @returns A derived async atom that resolves to an OptionSet or null.
 * @throws Error if the specified OptionSet is not found.
 */
export const optionSetAtomFamily = atomFamily((optionSetId: string | null) =>
  atom<OptionSet | null | undefined>((get) => {
    if (!optionSetId) return null;
    const surveyView: SurveyView | null | undefined = get(surveyViewAtom)?.data;

    // When the survey view isn't yet loaded, return null.
    if (!surveyView) return null;

    const optionSet: OptionSet | undefined = surveyView.optionSetMap.get(optionSetId);

    if (!optionSet) {
      console.error(`Failed to find OptionSet with ID "${optionSetId}".`);
      throw new Error('Unable to load OptionSet for the item. Please contact the survey administrator.');
    }

    return optionSet;
  })
);